import { trimText } from "../formatting/text";

import { type CombinedAttributeSet } from "@/schemas/attribute/AttributeSet";
import { UserBodyAttributeSet } from "@/schemas/profile/attributes/UserBodyAttributeSet";
import { UserEthnicityAttributeSet } from "@/schemas/profile/attributes/UserEthnicityAttributeSet";
import { UserHeightAttributeSet } from "@/schemas/profile/attributes/UserHeightAttributeSet";
import { UserRelationshipAttributeSet } from "@/schemas/profile/attributes/UserRelationshipAttributeSet";
import { UserRoleAttributeSet } from "@/schemas/profile/attributes/UserRoleAttributeSet";
import { UserWeightAttributeSet } from "@/schemas/profile/attributes/UserWeightAttributeSet";

export function formatAge(input: string | null, maxLength: number): string {
  if (input === null || input.trim() === "") {
    return "Not available";
  }

  const birthDate = new Date(input);
  if (isNaN(birthDate.getTime())) {
    return "Invalid Date";
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  if (age < 0) {
    return "Invalid Date";
  }

  const formattedAge = `${age} ${age === 1 ? "year" : "years"} old`;

  return trimText(formattedAge, maxLength);
}

function formatEnumAttribute(
  input: string | null,
  maxLength: number,
  attributeInfo: CombinedAttributeSet,
): string {
  if (
    input !== null &&
    input in attributeInfo.map &&
    attributeInfo.map[input]?.label
  ) {
    const item = attributeInfo.map[input];
    if (item !== undefined) {
      return trimText(item.label, maxLength);
    }
  }
  return "Not available";
}

export function formatRelationship(
  input: string | null,
  maxLength: number,
): string {
  return formatEnumAttribute(input, maxLength, UserRelationshipAttributeSet);
}

export function formatEthnicity(
  input: string | null,
  maxLength: number,
): string {
  return formatEnumAttribute(input, maxLength, UserEthnicityAttributeSet);
}

export function formatWeight(input: string | null, maxLength: number): string {
  return formatEnumAttribute(input, maxLength, UserWeightAttributeSet);
}

export function formatHeight(input: string | null, maxLength: number): string {
  return formatEnumAttribute(input, maxLength, UserHeightAttributeSet);
}

export function formatBody(input: string | null, maxLength: number): string {
  return formatEnumAttribute(input, maxLength, UserBodyAttributeSet);
}

export function formatRole(input: string | null, maxLength: number): string {
  return formatEnumAttribute(input, maxLength, UserRoleAttributeSet);
}

export function attributeListToMap<
  T extends string | number | symbol,
  V extends { id: T },
>(attributes: ReadonlyArray<V>): Readonly<Record<T, V>> {
  return Object.fromEntries(
    attributes.map((attribute) => [attribute.id, attribute]),
  ) as Readonly<Record<T, V>>;
}
